import { Box, Chip, Popover, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { FC, memo, useContext, useEffect, useState } from 'react';
import PlansContext from 'src/contexts/PlansContext';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import numberDecorator from 'src/decorators/number.decorator';
import { GarageEntity } from 'src/entities/Garage.entity';
import { PlanningsDeliveryEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';
import { datetimeUtil } from 'src/utils/datetime.util';

type Props = {
  truck: PlanningsTruckEntity;
  garage: GarageEntity;
  driver: PlanningsDriverEntity;
  deliveries: PlanningsDeliveryEntity[];
  infoPopoverAnchorEl: HTMLElement;
  handleInfoPanelClose: () => void;
}

const TruckInfoPanel: FC<Props> = memo(({
  truck,
  garage,
  driver,
  deliveries,
  infoPopoverAnchorEl,
  handleInfoPanelClose,
}) => {
  const plansContext = useContext(PlansContext);
  const { planningsOperationDeliveryByDeliveryIdEntity } = plansContext;
  const [sortedDeliveries, setSortedDeliveries] = useState<PlanningsDeliveryEntity[]>([]);

  useEffect(() => {
    setSortedDeliveries(
      deliveries.sort((a, b) => (new Date(a.startAt).getTime() - new Date(b.startAt).getTime()))
    );
  }, [deliveries]);

  const deliveryTable = (delivery: PlanningsDeliveryEntity) => {
    if (delivery.operations.length === 0) {
      return (
        <Table size="small" key={['empty-delivery-table', delivery.id].join('-')}>
          <TableBody>
            <TableRow>
              <TableCell>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{datetimeDecorator.toYyyyMmDd(new Date(delivery.startAt), '/')}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>-</Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      );
    }

    let wSeconds = 0;
    if (delivery.workStartAt && delivery.workEndAt) {
      wSeconds = datetimeUtil.diffSeconds(new Date(delivery.workStartAt), new Date(delivery.workEndAt));
    } else {
      const startAt = delivery.operations.sort((a, b) => new Date(a.arrivalAt).getTime() - new Date(b.arrivalAt).getTime())[0].arrivalAt;
      const endAt = delivery.operations.sort((a, b) => new Date(b.departureAt).getTime() - new Date(a.departureAt).getTime())[0].departureAt;
      wSeconds = datetimeUtil.diffSeconds(new Date(startAt), new Date(endAt));
    }
    const pSeconds = (delivery.workingAvailableDurationSeconds && delivery.workingAvailableDurationSeconds > 0)
      ? delivery.workingAvailableDurationSeconds
      : datetimeUtil.diffSeconds(new Date(delivery.startAt), new Date(delivery.endAt));
    const planningsOperationDelivery = planningsOperationDeliveryByDeliveryIdEntity[delivery.id];

    return (
      <Table size="small" key={['deliveeryTable', delivery.id].join('-')}>
        <TableBody>
          <TableRow>
            <TableCell colSpan={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{datetimeDecorator.toYyyyMmDd(new Date(delivery.startAt), '/')}</Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>積込件数:</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">
                {planningsOperationDelivery.loadPlaceCount}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>納品先件数:</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">{planningsOperationDelivery.unloadPlaceCount}</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>数量:</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">
                {planningsOperationDelivery.itemCount}
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>重量積載率:</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">
                {numberDecorator.toRoundedRate(planningsOperationDelivery.maxWeightG / truck.maximumLoadingCapacityWeightForCalculation, '')}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>体積積載率:</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">
                {(truck.loadingPlatformVolumeMm3 && truck.loadingPlatformVolumeMm3 > 0) ? numberDecorator.toRoundedRate(planningsOperationDelivery.maxVolumeMm3 / truck.loadingPlatformVolumeMm3, '') : '-'}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>稼働率:</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">
                {(pSeconds && pSeconds > 0) ? numberDecorator.toRoundedRate(wSeconds / pSeconds, '') : '-'}
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell colSpan={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                {`(回転数: ${planningsOperationDelivery.cycles.length})`}
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  };

  return (
    <Popover
      open={Boolean(infoPopoverAnchorEl)}
      anchorEl={infoPopoverAnchorEl}
      onClose={handleInfoPanelClose}
      anchorOrigin={{
        vertical: 'center',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'center',
        horizontal: 'left',
      }}
    >
      <Box sx={{ p: 2, overflow: 'auto' }}>
        {truck && (
          <Stack>
            <Typography variant="h6" sx={{ pb: 1 }}>車両情報</Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>トラック名</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{truck.temporary ? '指定なし' : (truck.licensePlateValue || '-')}</Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>車番</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{truck.truckNumber || '-'}</Typography>
                  </TableCell>
                </TableRow>
                {/* <TableRow>
                  <Typography variant="subtitle2" sx={{ p: 1, fontWeight: 'bold' }}>車両タイプ</Typography>
                  <Typography variant="body2" sx={{ p: 1 }}>
                    {truck.}
                  </Typography>
                </TableRow> */}
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>最大積載量</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {numberDecorator.convertGramToKg(truck.maximumLoadingCapacityWeightForCalculation)}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>最大積載体積</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {numberDecorator.convertMm3ToM3(truck.loadingPlatformVolumeMm3)}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>運送会社</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {truck.partnerCompanyName || '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>車庫</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {garage?.nullBase ? '拠点なし' : (garage?.name || '-')}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Stack>
        )}
        {driver && (
          <Stack>
            <Typography variant="h6" sx={{ py: 1 }}>ドライバー情報</Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>氏名</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{driver.temporary ? '指定なし' : (driver.name || '-')}</Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>電話番号</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{driver.phoneNumber || '-'}</Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>勤務可能時間帯</Typography>
                  </TableCell>
                  <TableCell>
                    {sortedDeliveries.map((delivery, idx) => (
                      <Stack direction="row" gap={1} key={['working-time-range', delivery.id, idx].join('-')}>
                        <Typography variant="body2" key={['delivery-date', idx].join('-')}>
                          {[datetimeDecorator.toYyyyMmDdHhMm(new Date(delivery.startAt), '/'), datetimeDecorator.toYyyyMmDdHhMm(new Date(delivery.endAt), '/')].join(' 〜 ')}
                          {(!delivery.returnToBase && '(帰庫しない)')}
                        </Typography>
                        {delivery.operations.some((it) => it.fixed) && (
                          <Chip color="info" size="small" label="確定" />
                        )}
                      </Stack>
                    ))}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Stack>
        )}
        {sortedDeliveries.length > 0 && (
          <Stack key="deliveriesTable">
            <Typography variant="h6" sx={{ py: 1 }}>運行ステータス</Typography>
            {sortedDeliveries.map((delivery) => (
              deliveryTable(delivery)
            ))}
          </Stack>
        )}
      </Box>
    </Popover>
  );
});

export default TruckInfoPanel;
